import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';

interface CreateUserDto {
    username: string;
    password: string;
    phone: string;
    /** Required for callers migrated to role selection (e.g. UserCreate). */
    roleIds?: string[];
    /** Required by the institution-user form: the profile name shown in the header and lists. */
    firstName?: string;
    lastName?: string;
}

export const UserService = {
    getUsers: async (page: number = 0, size: number = 10, search?: string) => {
        const params: Record<string, string | number> = { page, size };
        if (search && search.trim()) params.search = search.trim();
        const response = await axios.get(ENDPOINTS.USER.BASE, {
            params,
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    getInstitutionUsers: async (
        page: number = 0,
        size: number = 10,
        institutionId?: string,
        search?: string,
        activeOnly?: boolean
    ) => {
        const resolvedInstitutionId = institutionId || AuthService.getSelectedInstitution();
        const params: Record<string, string | number | boolean> = { page, size };
        if (activeOnly) params.activeOnly = true;
        if (search && search.trim()) params.search = search.trim();
        const response = await axios.get(`${ENDPOINTS.USER.INSTITUTION_USERS}/${resolvedInstitutionId}`, {
            params,
            headers: AuthService.getAuthHeader()
        });
        return response.data;
    },

    deleteUser: async (userId: string) => {
        const response = await axios.delete(
            ENDPOINTS.USER.DELETE(userId),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    createUser: async (userData: CreateUserDto) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.post(
            `${ENDPOINTS.USER.INSTITUTION_USERS}/${institutionId}`,
            userData,
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    toggleUserActive: async (userId: string) => {
        const response = await axios.patch(
            ENDPOINTS.USER.TOGGLE_ACTIVE(userId),
            {},
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    checkUsernameAvailable: async (username: string, excludeUserId: string): Promise<boolean> => {
        const response = await axios.get(ENDPOINTS.USER.CHECK_USERNAME, {
            params: { username, excludeUserId },
            headers: AuthService.getAuthHeader()
        });
        return response.data.available;
    },

    updateUsername: async (userId: string, username: string) => {
        const response = await axios.patch(
            ENDPOINTS.USER.UPDATE_USERNAME(userId),
            { username },
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    resetUserPassword: async (userId: string, institutionId?: string) => {
        const resolvedInstitutionId = institutionId || AuthService.getSelectedInstitution();
        const response = await axios.patch(
            ENDPOINTS.USER.RESET_PASSWORD(userId, resolvedInstitutionId),
            {},
            { headers: AuthService.getAuthHeader() }
        );
        return response.data;
    },

    resetTwoFactor: async (userId: string): Promise<void> => {
        await axios.post(ENDPOINTS.TWO_FACTOR.ADMIN_RESET(userId), {}, { headers: AuthService.getAuthHeader() });
    },

    blockBackupCodes: async (userId: string): Promise<void> => {
        await axios.post(ENDPOINTS.TWO_FACTOR.ADMIN_BLOCK_BACKUP_CODES(userId), {}, { headers: AuthService.getAuthHeader() });
    },

    unblockBackupCodes: async (userId: string): Promise<void> => {
        await axios.post(ENDPOINTS.TWO_FACTOR.ADMIN_UNBLOCK_BACKUP_CODES(userId), {}, { headers: AuthService.getAuthHeader() });
    }
};
